import type { RefInputType } from '@arco-design/web-react/es/Input';
import type { PasswordProps } from '../const';
import { Input } from '@arco-design/web-react';
import { cn } from '@utils';
import { IconEyeInvisible, IconEyeVisible, IconUpperCase } from '@wegic/icon';
import { forwardRef, useCallback, useMemo, useState } from 'react';

import './index.less';

function Password(props: PasswordProps, ref: React.Ref<RefInputType>) {
  const {
    prefix,
    variant = 'gray',
    size = 'default',
    className,
    placeholder = 'Please enter password',
    onChange,
    onVisibilityChange,
    onKeyUp,
    onKeyDown,
    onClick,
    ...restProps
  } = props;

  const [showPassword, setShowPassword] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [capsLock, setCapsLock] = useState(false);

  const handlePasswordChange = useCallback(
    (val: string, e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(val);
      onChange?.(val, e);
    },
    [onChange],
  );

  const handleKeyUp = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const capsLockOn = e.getModifierState('CapsLock');
      setCapsLock(capsLockOn);
      onKeyUp?.(e);
    },
    [onKeyUp],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const capsLockOn = e.getModifierState('CapsLock');
      setCapsLock(capsLockOn);
      onKeyDown?.(e);
    },
    [onKeyDown],
  );

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLInputElement>) => {
      const capsLockOn = e.getModifierState('CapsLock');
      setCapsLock(capsLockOn);
      onClick?.(e);
    },
    [onClick],
  );

  const togglePasswordVisibility = useCallback(() => {
    const newVisibility = !showPassword;
    setShowPassword(newVisibility);
    onVisibilityChange?.(newVisibility);
  }, [showPassword, onVisibilityChange]);

  const suffixContent = useMemo(() => {
    if (!inputValue) {
      // 不能返回 null，arco input结构不一样影响样式生效
      return <></>;
    }

    return (
      <div className="wg-input-password__suffix">
        <div className="wg-input-password__caps-lock" style={{ opacity: capsLock ? 1 : 0 }}>
          <IconUpperCase />
        </div>

        <button
          type="button"
          onClick={togglePasswordVisibility}
          className="wg-input-password__icon"
          tabIndex={-1}
          aria-label={showPassword ? 'Hide password' : 'Show password'}
        >
          {showPassword ? <IconEyeInvisible /> : <IconEyeVisible />}
        </button>
      </div>
    );
  }, [inputValue, capsLock, showPassword, togglePasswordVisibility]);

  const inputClass = cn(
    'wg-input',
    'wg-input-password',
    `wg-input--${size}`,
    `wg-input--${variant}`,
    {
      'wg-input--prefix': !!prefix,
    },
    className,
  );

  return (
    <Input
      ref={ref}
      type={showPassword ? 'text' : 'password'}
      placeholder={placeholder}
      className={inputClass}
      size={size}
      prefix={prefix || <></>}
      onChange={handlePasswordChange}
      onKeyUp={handleKeyUp}
      onKeyDown={handleKeyDown}
      onClick={handleClick}
      suffix={suffixContent}
      {...restProps}
    />
  );
}

const PasswordComponent = forwardRef<RefInputType, PasswordProps>(Password);

PasswordComponent.displayName = 'Password';

export default PasswordComponent;
